/**
 * CashInventory — Phase 86.K: "What you have", split into two inventory lists.
 *
 * SCOPE RULE, deliberately explicit because getting it wrong is dangerous:
 * the sub-tabs are INVENTORY FILTERS, NOT PLANNING SCOPES. Selecting
 * "Cash & wallets" must never change the four-number strip, available for
 * bills, assigned to priorities, the safety buffer, the cash plan, the next 30
 * days, the unassigned-cash decision or goal funding — otherwise a user with
 * ₹19L in the bank and ₹18k in wallets would be shown a dangerously low buffer.
 *
 * So the filtered figures live in their own variables (`scopedRows`,
 * `scopedCount`, `scopedTotal`) and are read NOWHERE else. Everything global
 * comes from `useCashPlan()`, which is never given the filter.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Upload, RefreshCw, Target, Users, AlertTriangle, PenLine, Wallet,
} from 'lucide-react';
import SubTabs from '@/components/accounts/SubTabs';
import AccountAvatar from '@/components/accounts/AccountAvatar';
import { FreshnessChip } from '@/components/accounts/FreshnessChip';
import EmptyState from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { formatMoney } from '@/lib/format';
import { useCurrencyContext } from '@/hooks/useCurrencyContext';
import { useCashPlan } from '@/components/cash/CashPlanSections';
import AssignJobsDialog from '@/components/cash/AssignJobsDialog';

const WALLET_TYPES = new Set(['wallet']);          // normalised type from useAccountsData
const UNCONFIRMED_DAYS = 30;

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const daysSince = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};

function Meta({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
      {items.map((node, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="w-0.5 h-0.5 rounded-full bg-muted-foreground/40 shrink-0" />}
          {node}
        </React.Fragment>
      ))}
    </div>
  );
}

function RowAction({ icon: Icon, label, onClick, testid, urgent }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
        urgent
          ? 'border-gold/50 bg-gold/15 text-[#7A4E08] font-medium hover:bg-gold/25'
          : 'border-border text-muted-foreground hover:text-evergreen hover:border-evergreen/40'}`}
      data-testid={testid}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </button>
  );
}

function AccountCard({ account, planRow, baseCurrency, isWallet, onOpen, actions }) {
  const ctx = useCurrencyContext();
  const native = Math.abs(account.balance || 0);
  const currency = account.currency || baseCurrency;
  const converted = planRow?.display_balance != null
    ? planRow.display_balance
    : (currency !== baseCurrency && (currency in (ctx.ratesToBase || {}))
      ? ctx.toBase(native, currency) : null);
  const asOf = planRow?.as_of || account.last_updated;
  const coverage = planRow?.coverage;
  const purposeLabel = planRow
    ? (planRow.purposes?.length
      ? planRow.purposes.map((p) => p.label + (p.goal_name ? ` → ${p.goal_name}` : '')).join(' + ')
      : (planRow.primary_purpose_label || 'No purpose assigned'))
    : null;
  const stale = daysSince(asOf);

  return (
    <div
      role="button"
      tabIndex={0}
      // The row is a click surface, but it holds its own buttons. A click that
      // started on one of those — or an Enter that lands on one after a dialog
      // closes and returns focus — must never navigate away.
      onClick={(e) => {
        if (e.target.closest('button, a, input, select, [role="dialog"]')) return;
        onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' || e.target !== e.currentTarget) return;
        onOpen();
      }}
      className="px-5 py-4 hover:bg-offwhite/60 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      data-testid={`cash-inventory-row-${account.id}`}
    >
      <div className="flex items-start gap-4">
        <AccountAvatar account={account} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-charcoal leading-none truncate">{account.name}</p>
            {account.is_joint && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                    data-testid={`cash-inventory-joint-${account.id}`}>
                <Users className="h-2.5 w-2.5" aria-hidden="true" /> Joint
              </span>
            )}
          </div>

          <div className="mt-1.5 space-y-1">
            <Meta>
              {account.institution ? <span className="truncate">{account.institution}</span> : null}
              <span>{account.subtype_label || planRow?.type_label || 'Account'}</span>
              {purposeLabel ? <span data-testid={`cash-inventory-purpose-${account.id}`}>{purposeLabel}</span> : null}
            </Meta>
            <Meta>
              {isWallet
                ? <span data-testid={`cash-inventory-confirmed-${account.id}`}>
                    {asOf ? `Confirmed ${fmtDate(asOf)}` : 'Never confirmed'}
                  </span>
                : <span data-testid={`cash-inventory-asof-${account.id}`}>
                    {asOf ? `Balance as of ${fmtDate(asOf)}` : 'No balance date'}
                  </span>}
              {planRow?.freshness?.latest_txn && !isWallet
                ? <span>Latest activity {fmtDate(planRow.freshness.latest_txn)}</span> : null}
              {isWallet
                ? (coverage
                  ? <span data-testid={`cash-inventory-tracked-${account.id}`}>
                      {coverage.tracked ? 'Transactions tracked' : 'Transactions not tracked'}
                    </span>
                  : null)
                : (coverage
                  ? <span data-testid={`cash-inventory-coverage-${account.id}`}>{coverage.label}</span>
                  : null)}
            </Meta>
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-2">
            <FreshnessChip freshness={planRow?.freshness} />
            {planRow?.freshness?.trusted === false && planRow.freshness.detail && (
              <span className="text-[11px] text-muted-foreground"
                    data-testid={`cash-inventory-stale-detail-${account.id}`}>
                {planRow.freshness.detail} Confirming it unblocks your 30-day view.
              </span>
            )}
            {isWallet && !planRow?.freshness?.status && stale != null && stale > UNCONFIRMED_DAYS && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#B07210]/25 bg-[#B07210]/10 px-2 py-0.5 text-[10px] font-medium text-[#8A5A0C]"
                    data-testid={`cash-inventory-unconfirmed-${account.id}`}>
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                Not confirmed in {stale} days
              </span>
            )}
            {planRow?.linked_goals?.map((g) => (
              <span key={g} className="inline-flex items-center gap-1 rounded-full bg-sage-light/60 px-2 py-0.5 text-[10px] text-evergreen"
                    data-testid={`cash-inventory-goal-${account.id}`}>
                <Target className="h-2.5 w-2.5" aria-hidden="true" /> {g}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap mt-3">{actions}</div>
        </div>

        <div className="text-right shrink-0">
          <p className="num text-sm font-semibold text-charcoal tabular-nums"
             data-testid={`cash-inventory-balance-${account.id}`}>
            {formatMoney(native, currency)}
          </p>
          {converted != null && currency !== baseCurrency && (
            <p className="text-[11px] text-muted-foreground tabular-nums mt-0.5"
               data-testid={`cash-inventory-converted-${account.id}`}>
              ≈ {formatMoney(converted, baseCurrency)}
            </p>
          )}
          {planRow?.unassigned_amount > 0 && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {formatMoney(planRow.unassigned_amount, currency)} unassigned
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CashInventory({
  accounts = [],
  baseCurrency = 'INR',
  onAdd,                 // (type) => void
  onSelectAccount,
  onSetBalance,
}) {
  const [filter, setFilter] = useState('all');
  const [assigning, setAssigning] = useState(null);
  const navigate = useNavigate();
  const ctx = useCurrencyContext();
  const { plan, setPlan } = useCashPlan();

  const cash = useMemo(() => accounts.filter((a) => a.category === 'cash'), [accounts]);
  const banks = useMemo(() => cash.filter((a) => !WALLET_TYPES.has(a.type)), [cash]);
  const wallets = useMemo(() => cash.filter((a) => WALLET_TYPES.has(a.type)), [cash]);

  const planRows = useMemo(() => {
    const map = {};
    for (const r of plan?.accounts || []) map[String(r.id)] = r;
    return map;
  }, [plan]);

  /* ---- FILTERED FIGURES. Local to this list. Never read by the plan. ---- */
  const scopedRows = filter === 'savings' ? banks : filter === 'wallet' ? wallets : cash;
  const scopedCount = scopedRows.length;
  const scopedCurrencies = new Set(scopedRows.map((a) => a.currency || baseCurrency));
  // When everything in view shares one currency, report it in THAT currency —
  // converting a single-currency list to the reporting currency adds a rate the
  // user never asked for. Only a mixed list gets converted, and it says so.
  const scopedMixed = scopedCurrencies.size > 1;
  const scopedCurrency = scopedMixed ? baseCurrency : ([...scopedCurrencies][0] || baseCurrency);
  const scopedTotal = scopedRows.reduce((sum, a) => {
    const native = Math.abs(a.balance || 0);
    return sum + (scopedMixed ? ctx.toBase(native, a.currency || baseCurrency) : native);
  }, 0);
  const scopedLatestConfirmed = scopedRows
    .map((a) => planRows[String(a.id)]?.as_of || a.last_updated)
    .filter(Boolean)
    .sort()
    .slice(-1)[0];
  /* ---------------------------------------------------------------------- */

  const unconfirmedWallets = wallets.filter((a) => {
    const d = daysSince(planRows[String(a.id)]?.as_of || a.last_updated);
    return d == null || d > UNCONFIRMED_DAYS;
  });
  const unconfirmedCurrencies = new Set(unconfirmedWallets.map((a) => a.currency || baseCurrency));
  const unconfirmedMixed = unconfirmedCurrencies.size > 1;
  const unconfirmedCurrency = unconfirmedMixed
    ? baseCurrency : ([...unconfirmedCurrencies][0] || baseCurrency);
  const unconfirmedTotal = unconfirmedWallets.reduce((sum, a) => {
    const native = Math.abs(a.balance || 0);
    return sum + (unconfirmedMixed ? ctx.toBase(native, a.currency || baseCurrency) : native);
  }, 0);

  const noun = filter === 'savings' ? 'bank account' : filter === 'wallet' ? 'wallet' : 'cash account';

  const openAssign = (account) => {
    const row = planRows[String(account.id)];
    if (row) setAssigning(row);
    else onSelectAccount?.(account);
  };

  const rowActions = (account, isWallet) => {
    // A balance the account's own transactions have already overtaken is the one
    // thing holding back the 30-day view, so this stops being a quiet option.
    const untrusted = planRows[String(account.id)]?.freshness?.trusted === false;
    return (
    <>
      <RowAction icon={RefreshCw} urgent={untrusted}
                 label={untrusted ? 'Update this balance' : 'Update balance'}
                 testid={`cash-inventory-update-${account.id}`}
                 onClick={() => onSetBalance?.(account)} />
      {isWallet ? (
        <RowAction icon={PenLine} label="Record transaction" testid={`cash-inventory-record-${account.id}`}
                   onClick={() => navigate(`/transactions?new=1&account_id=${account.id}&account=${encodeURIComponent(account.name)}`)} />
      ) : (
        <RowAction icon={Upload} label="Import statement" testid={`cash-inventory-import-${account.id}`}
                   onClick={() => navigate('/import')} />
      )}
      <RowAction icon={Target} label="Assign a purpose" testid={`cash-inventory-assign-${account.id}`}
                 onClick={() => openAssign(account)} />
    </>
    );
  };

  return (
    <div data-testid="cash-inventory">
      <SubTabs
        tabs={[
          { id: 'all', label: 'All', count: cash.length },
          { id: 'savings', label: 'Bank & savings', count: banks.length },
          { id: 'wallet', label: 'Cash & wallets', count: wallets.length },
        ]}
        active={filter}
        onChange={setFilter}
        testidPrefix="accounts-cash-filter"
      />

      {/* Scoped line — describes the LIST only. */}
      {scopedCount > 0 && (
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-1">
        <p className="text-sm text-muted-foreground" data-testid="cash-inventory-scope">
          {scopedCount} {noun}{scopedCount === 1 ? '' : 's'}
          {' · '}
          <span className="num font-semibold text-charcoal">
            {(scopedMixed && !ctx.isReady)
              ? '—'
              : `${formatMoney(scopedTotal, scopedCurrency)} reported total`}
          </span>
        </p>
        {scopedLatestConfirmed && (
          <p className="text-xs text-muted-foreground" data-testid="cash-inventory-latest-confirmed">
            Latest confirmed balance: {fmtDate(scopedLatestConfirmed)}
          </p>
        )}
      </div>
      )}
      {scopedMixed && (
        <p className="text-[11px] text-muted-foreground mb-1" data-testid="cash-inventory-fx-note">
          Held in {[...scopedCurrencies].sort().join(' and ')}
          {'; '}
          {[...scopedCurrencies].filter((c) => c !== baseCurrency).sort().join(' and ')} balances are
          converted to {baseCurrency} for this total
          {ctx.asOfDate ? ` at the ${fmtDate(ctx.asOfDate)} rate` : ''}. Each row keeps its own currency.
        </p>
      )}
      {filter !== 'all' && (
        <p className="text-[11px] text-muted-foreground mb-4" data-testid="cash-inventory-scope-note">
          This filters the list only. Your cash plan below still includes every eligible cash account.
        </p>
      )}
      {(filter === 'all' || scopedCount === 0) && <div className="mb-4" />}

      {/* Wallets that nobody has confirmed in a while — a real risk to the total. */}
      {unconfirmedWallets.length > 0 && filter !== 'savings' && (!unconfirmedMixed || ctx.isReady) && (
        <div className="flex items-start gap-2 rounded-xl border border-[#B07210]/25 bg-[#B07210]/10 px-4 py-3 mb-4 text-xs text-[#8A5A0C]"
             data-testid="cash-inventory-unconfirmed-warning">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          <p>
            {formatMoney(unconfirmedTotal, unconfirmedCurrency)} across {unconfirmedWallets.length}{' '}
            {unconfirmedWallets.length === 1 ? 'wallet has' : 'wallets has'} not been confirmed in more
            than {UNCONFIRMED_DAYS} days. Update the balance so the totals above can be trusted.
          </p>
        </div>
      )}

      <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
        {cash.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title="Start with your first cash account"
            body="Add your salary account, savings, wallets — anything you use to hold money. This is where 2120 tracks your day-to-day balance."
            primaryCta={{ label: 'Add bank account', onClick: () => onAdd?.('bank'), testid: 'accounts-cash-empty-primary' }}
            secondaryCta={{ label: 'Explore Demo Account', onClick: () => navigate('/settings?tab=demo'), testid: 'accounts-cash-empty-secondary' }}
            testid="accounts-cash-empty"
            className="border-0 shadow-none"
          />
        ) : scopedRows.length === 0 ? (
          filter === 'wallet' ? (
            <EmptyState
              icon={Wallet}
              title="No cash or stored-value wallets yet"
              body="Add physical cash, foreign currency or digital wallets that hold their own balance. Payment apps connected to an existing bank account do not need to be added separately."
              primaryCta={{ label: 'Add cash or wallet', onClick: () => onAdd?.('wallet'), testid: 'cash-inventory-wallet-empty-cta' }}
              testid="cash-inventory-wallet-empty"
              className="border-0 shadow-none"
            />
          ) : (
            <EmptyState
              icon={Wallet}
              title="No bank or savings accounts yet"
              body="Add a current, savings or money-market account to see your everyday balance, import statements and build a safety reserve."
              primaryCta={{ label: 'Add bank account', onClick: () => onAdd?.('bank'), testid: 'cash-inventory-bank-empty-cta' }}
              testid="cash-inventory-bank-empty"
              className="border-0 shadow-none"
            />
          )
        ) : (
          <div className="divide-y divide-border/40">
            {scopedRows.map((a) => {
              const isWallet = WALLET_TYPES.has(a.type);
              return (
                <AccountCard
                  key={a.id}
                  account={a}
                  planRow={planRows[String(a.id)]}
                  baseCurrency={baseCurrency}
                  isWallet={isWallet}
                  onOpen={() => onSelectAccount?.(a)}
                  actions={rowActions(a, isWallet)}
                />
              );
            })}
          </div>
        )}

        {cash.length > 0 && scopedRows.length > 0 && (
          <div className="px-5 py-3 border-t border-border/60 bg-offwhite/40 flex flex-wrap items-center gap-4">
            {filter !== 'wallet' && (
              <button type="button" onClick={() => onAdd?.('bank')}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-evergreen font-medium transition-colors"
                      data-testid="cash-inventory-add-bank">
                <Plus className="h-3.5 w-3.5" /> Add bank account
              </button>
            )}
            {filter !== 'savings' && (
              <button type="button" onClick={() => onAdd?.('wallet')}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-evergreen font-medium transition-colors"
                      data-testid="cash-inventory-add-wallet">
                <Plus className="h-3.5 w-3.5" /> Add cash or wallet
              </button>
            )}
            {filter !== 'wallet' && (
              <button type="button" onClick={() => navigate('/import')}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-evergreen font-medium transition-colors"
                      data-testid="cash-inventory-import-statement">
                <Upload className="h-3.5 w-3.5" /> Import statement
              </button>
            )}
          </div>
        )}
      </div>

      {assigning && plan && (
        <AssignJobsDialog
          key={assigning.id}
          account={assigning}
          plan={plan}
          open
          onOpenChange={(v) => { if (!v) setAssigning(null); }}
          onSaved={(next) => setPlan(next)}
        />
      )}
    </div>
  );
}
